import React from "react"
import { useParams } from "react-router-dom"
import styled from "styled-components"
import { getProjectDetails } from "services/projectsService"
import LoadingIcon from "components/common/LoadingIcon"
import useFetch from "hooks/useFetch"
import ScrollingGallery from "../../components/common/ScrollingGallery.jsx";
import WhyChooseUs from "components/core/WhyChooseUs"

function ProjectDetailsPage() {
  let { slug } = useParams()
  const { data, isLoading } = useFetch(() => getProjectDetails(slug))

  const hasGalleryImages = data?.gallery && Array.isArray(data.gallery) && data.gallery.length > 0;
  const gallerySection = hasGalleryImages ? <ScrollingGallery images={data.gallery} /> : <p>No images available</p>;


  if (isLoading) return <LoadingIcon isFullScreen />
  if (!data) return <p>Error loading project details.</p>

  return (
    <Container data-scroll-container>
      <HeroContainer>
        <HeroImage
          src={data?.hero || "default-image.jpg"}
          alt={data?.title || "Project Image"}
        />
        <ProjectDetailsWrapper>
          <ProjectTitle>{data?.title || "Untitled Project"}</ProjectTitle>
          <ProjectDescription>
            {data?.description || "No description available."}
          </ProjectDescription>
          {data?.table && (
            <ProjectTable>
              <thead>
                <tr>
                  {data.table.headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {data.table.details.map((detail, index) => (
                    <td key={index}>{detail}</td>
                  ))}
                </tr>
              </tbody>
            </ProjectTable>
          )}
        </ProjectDetailsWrapper>
      </HeroContainer>
      <ContentContainer>

        {data?.quoteRight && (
          <RightAlignedQuote>{data.quoteRight}</RightAlignedQuote>
        )}
   
        <DetailSection>
        <div>
          {hasGalleryImages ? <ScrollingGallery images={data.gallery} /> : <p>No images available</p>}
        </div>
        </DetailSection>

        {/* Bold center-justified quote */}
        {data?.quoteCenter && <CenteredQuote>{data.quoteCenter}</CenteredQuote>}

        {(data?.detailOne || data?.detailTwo) && (
          <GridSection>
            {data?.detailOne && (
              <>
                <div /> {/* Empty first column */}
                <TextWrapper style={{ textAlign: 'right' }}>
                  <Title>{data.detailOne.title}</Title>
                  <Description>{data.detailOne.text}</Description>
                </TextWrapper>
                <ImageWrapper>
                  <SectionImage src={data.detailOne.image} alt="Detail One" />
                </ImageWrapper>
              </>
            )}

            {data?.detailTwo && (
              <>
                <div /> {/* Empty first column */}
                <TextWrapper style={{ textAlign: 'right' }}>
                  <Title>{data.detailTwo.title}</Title>
                  <Description>{data.detailTwo.text}</Description>
                </TextWrapper>
                <ImageWrapper>
                  <SectionImage src={data.detailTwo.image} alt="Detail Two" />
                </ImageWrapper>
              </>
            )}
          </GridSection>
        )}

        {data?.inspirationSection && (
          <InspirationSection>
            <InspirationContent>
              <InspirationTitle>
                {data.inspirationSection.title}
              </InspirationTitle>
              <InspirationDescription>
                {data.inspirationSection.description}
              </InspirationDescription>
            </InspirationContent>
          </InspirationSection>
        )}

        {data?.inspirationBoard && (
          <InspirationBoard>
            <BoardImage src={data.inspirationBoard} alt="Inspiration Board" />
          </InspirationBoard>
        )}
      </ContentContainer>
      <WhyChooseUs />
    </Container>
    
  )
}

export default ProjectDetailsPage

const Container = styled.div`
  padding: 36px 5%;
`

const ContentContainer = styled.div`
  padding: 14px 5%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const HeroContainer = styled.section`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 5%;
  paddingTop: 0px;
  text-align: center;
`

const HeroImage = styled.img`
  width: 100%;
  max-width: 1200px;
  height: auto;
  padding: 10px;
  border-radius: 20px;
  object-fit: cover;
`

const ProjectDetailsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 40px;
  width: 100%;
  max-width: 1600px;
  margin-top: 40px;
  align-items: center; // Center horizontally

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`

const ProjectTitle = styled.h1`
  font-size: 48px;
  font-weight: bold;
  text-transform: uppercase;
  text-align: left; // Ensure text is left-aligned
  flex: 1;

  @media (max-width: 768px) {
    font-size: 32px;
    text-align: left; // Keep it left-aligned on mobile too
  }
`

const ProjectDescription = styled.p`
  font-size: 18px;
  color: #555;
  line-height: 1.6;
  text-align: left;  // Center the text itself
  max-width: 800px;  // Prevents it from being too wide
  margin: 0 left;
  padding 0;
`

const RightAlignedQuote = styled.blockquote`
  font-size: 36px;
  font-style: italic;
  text-align: right;
  margin: 96px 5% 96px auto;
  max-width: 70%;
  color: #666;
  padding: 0px;
`

const CenteredQuote = styled.blockquote`
  font-size: 48px;
  font-weight: bold;
  text-align: center;
  margin: 240px 5% 120px 5%;
  // max-width: 70%;
  color: #333;
`

const DetailSection = styled.section`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 40px;
  padding: 60px 5%;
  //max-width: 1200px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`
const TextWrapper = styled.div`
  flex: 1;
  max-width: 500px;
`

const Title = styled.h2`
  font-size: 36px;
  font-weight: bold;
  margin-bottom: 20px;
`

const Description = styled.p`
  font-size: 18px;
  line-height: 1.6;
  color: #555;
`

const ImageWrapper = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-end;
`

const SectionImage = styled.img`
  width: 100%;
  //max-width: 500px;
  height: auto;
  border-radius: 10px;
`

const InspirationSection = styled.section`
  display: flex;
  justify-content: space-between;
  align-items: center top;
  padding: 80px 5%;
  gap: 40px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const InspirationContent = styled.div`
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: top;
`

const InspirationTitle = styled.h2`
  flex: 1;
  font-size: 72px;
  font-weight: bold;
  text-align: left;
`

const InspirationDescription = styled.p`
  flex: 1;
  font-size: 18px;
  color: #555;
  line-height: 1.6;
`

const InspirationBoard = styled.section`
  width: 100%;
  padding: 40px 5%;
  display: flex;
  justify-content: center;
`

const BoardImage = styled.img`
  width: 80%;
  max-width: 1600px;
  height: auto;
  border-radius: 10px;
  object-fit: cover;
`

const ProjectTable = styled.table`
  width: 100%;
  max-width: 800px;
  border-collapse: collapse;

  th,
  td {
    padding: 6px 10px;
    text-align: left;
    vertical-align: top;
    background-color: transparent;
  }

  th {
    font-weight: bold;
  }

  tr {
    border-bottom: 1px solid #ddd;
  }

  tbody tr:last-child {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    th,
    td {
      padding: 5px 8px;
    }
  }
`
const GridSection = styled.section`
  display: grid;
  grid-template-columns: 1fr 1.5fr 1.5fr;
  gap: 40px 20px;
  padding: 60px 5%;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`
